import styled from '@emotion/styled';
import { ApiFilterCategory, ApiNumberRangeFilter, ApiPriceFromJSONTyped } from '@ibe/api';
import React, { useEffect, useState, JSX } from 'react';
import ReactSlider from 'react-slider';
import { Row } from 'reactstrap';
import fallback from '../../../../_Translations/generated/filter-range.en.t.json';
import Keys from '../../../../_Translations/generated/filter-range.en.t.json.keys';
import useTranslation from '../../../Hooks/useTranslation';
import { Nbsp } from '../../UI/Nbsp';
import { Price } from '../../UI/Price/Price';
import useConfig from '../../../Hooks/useConfig';
import { roundToNearest } from '../../../../Utils/globals';
import { FilterOverrides } from './FilterOptions';

export interface FilterRangeProps {
  filter: ApiNumberRangeFilter;
  triggerFilterChanged: (min: number, max: number, filter: ApiNumberRangeFilter) => void;
  filterOverrides?: FilterOverrides;
}

const StyledThumb = styled.div`
  :focus {
    outline: none;
  }
`;

const StyledTrack = styled.div``;

const Track = (): JSX.Element => {
  return <StyledTrack key={`StyledTrack_${Math.random()}`} className="iso__filterRange__track" />;
};

const FilterRange = (props: FilterRangeProps): JSX.Element => {
  const { t } = useTranslation('filter-range', fallback, Keys);
  const config = useConfig();
  const { filter, triggerFilterChanged, filterOverrides } = props;
  const [minSelected, setMinSelected] = useState(filter.minSelected || filter.min);
  const [maxSelected, setMaxSelected] = useState(filter.maxSelected || filter.max);
  const { min: minValue, max: maxValue, unit, category } = filter;
  const min = filterOverrides?.filterRange?.roundMinMax
    ? roundToNearest(minValue, filterOverrides?.filterRange?.roundMinMax, 'DOWN')
    : minValue;
  const max = filterOverrides?.filterRange?.roundMinMax
    ? roundToNearest(maxValue, filterOverrides?.filterRange?.roundMinMax, 'UP')
    : maxValue;

  const steps =
    filterOverrides?.filterRange?.steps ||
    (category === ApiFilterCategory.DURATION ||
    category === ApiFilterCategory.SEATSCOUNT ||
    category === ApiFilterCategory.BAGSCOUNT ||
    category === ApiFilterCategory.DOORSCOUNT
      ? 1
      : 0.01);

  useEffect(() => {
    if (filter) {
      setMinSelected(filter.minSelected || 0);
      setMaxSelected(filter.maxSelected || 0);
    }
  }, [filter]);

  const Thumb = (
    thumbProps: object,
    state: { index: number; value: number | number[]; valueNow: number }
  ): JSX.Element => {
    const isReachingMax: boolean =
      state.index === 0 && state.valueNow >= max - Math.ceil(Math.log2(max - min));

    return (
      <StyledThumb
        // Can not do it, otherwise thumbs won't render properly, see:https://zillow.github.io/react-slider/ (renderThumb)
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...thumbProps}
        className={
          isReachingMax
            ? 'iso__filterRange__thumb iso__filterRange__thumb__max'
            : 'iso__filterRange__thumb'
        }
      />
    );
  };

  const onChange = (value: number | number[] | null | undefined): void => {
    if (Array.isArray(value) && value.length === 2) {
      setMinSelected(value[0]);
      setMaxSelected(value[1]);
    }
  };

  const onAfterChange = (value: number | number[] | null | undefined): void => {
    if (Array.isArray(value) && value.length === 2) {
      triggerFilterChanged(value[0], value[1], filter);
    }
  };

  function minutesToHours(minutes: number): number | string {
    const mins = minutes % 60;
    const hours = Math.floor(minutes / 60);

    return `${hours}h ${mins}m`;
  }

  function renderLabelByFilterCategory(): JSX.Element {
    if (category === ApiFilterCategory.PRICERANGE) {
      return (
        <Row className="ml-0">
          <Price
            price={ApiPriceFromJSONTyped(
              {
                currencyCode: unit,
                modifiers: [],
                finalPrice: minSelected || min
              },
              false
            )}
            roundPrice={
              !!filterOverrides?.filterRange?.roundMinMax
                ? 'ROUND_DOWN_MODE'
                : config.roundPricesToWholeNumber
            }
          />
          <Nbsp />
          {' - '}
          <Nbsp />
          <Price
            price={ApiPriceFromJSONTyped(
              {
                currencyCode: unit,
                modifiers: [],
                finalPrice: maxSelected || max
              },
              false
            )}
            roundPrice={
              !!filterOverrides?.filterRange?.roundMinMax
                ? 'ROUND_DOWN_MODE'
                : config.roundPricesToWholeNumber
            }
          />
        </Row>
      );
    }
    if (category === ApiFilterCategory.DURATION && unit === 'HOURS-MINUTES') {
      return (
        <div>
          {minSelected ? minutesToHours(minSelected) : minutesToHours(min)} {' - '}{' '}
          {maxSelected ? minutesToHours(maxSelected) : minutesToHours(max)}{' '}
        </div>
      );
    }
    if (category === ApiFilterCategory.DURATION) {
      return (
        <div>
          {minSelected || min} {' - '} {maxSelected || max} {t(`${category}_${unit}`)}
        </div>
      );
    }
    if (category === ApiFilterCategory.DISTANCELIMIT) {
      return (
        <div>
          {minSelected || min} {t(`${category}_${unit}`)} {' - '} {maxSelected || max}{' '}
          {t(`${category}_${unit}`)}
        </div>
      );
    }
    return (
      <div>
        {' '}
        {minSelected || min} {' - '} {maxSelected || max}
      </div>
    );
  }

  return (
    <>
      <div className="iso__filterRange__values ml-1">{renderLabelByFilterCategory()}</div>
      <div className="iso__filterRange__sliderWrapper">
        <ReactSlider
          defaultValue={[min, max]}
          value={[minSelected || min, maxSelected || max]}
          onChange={(value: number | number[] | null | undefined): void => onChange(value)}
          onAfterChange={(value: number | number[] | null | undefined): void =>
            onAfterChange(value)
          }
          min={min}
          max={max}
          className="iso__filterRange__sliderContainer"
          orientation="horizontal"
          renderTrack={Track}
          renderThumb={Thumb}
          step={steps}
        />
      </div>
    </>
  );
};

export default FilterRange;
